import React, { useState } from 'react'
import { Link } from '@inertiajs/react'

interface HeatmapCell {
  date: string
  occ: number
  booked: number
  capacity: number
}

interface HeatmapRow {
  propertyId: number
  propertyName: string
  /** The canonical path of this row's hotel — backend-served, never rebuilt from id. */
  propertyBasePath: string
  cells: HeatmapCell[]
}

interface HeatmapData {
  days: string[]
  rows: HeatmapRow[]
}

interface ForwardHeatmapProps {
  data: HeatmapData
}

// Color stops: pure white at 0%, teal saturation builds toward 100%.
// Editorial palette — clean linear ramp that reads at a glance.
function cellColor(occ: number): { bg: string; text: string } {
  if (occ <= 0) return { bg: 'bg-slate-50', text: 'text-slate-300' }
  if (occ < 25) return { bg: 'bg-teal-50', text: 'text-teal-700' }
  if (occ < 50) return { bg: 'bg-teal-100', text: 'text-teal-800' }
  if (occ < 70) return { bg: 'bg-teal-200', text: 'text-teal-900' }
  if (occ < 85) return { bg: 'bg-teal-400', text: 'text-white' }
  if (occ < 100) return { bg: 'bg-teal-600', text: 'text-white' }
  return { bg: 'bg-teal-800', text: 'text-white' }
}

function formatDayShort(iso: string): { dow: string; day: string; isWeekend: boolean; isToday: boolean } {
  const date = new Date(iso + 'T00:00:00')
  const dow = date.toLocaleDateString('en-GB', { weekday: 'short' })[0]
  const day = date.getDate().toString()
  const weekday = date.getDay()
  const today = new Date()
  const isToday =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  return { dow, day, isWeekend: weekday === 0 || weekday === 6, isToday }
}

export default function ForwardHeatmap({ data }: ForwardHeatmapProps) {
  const [hover, setHover] = useState<{
    rowIdx: number
    colIdx: number
    cell: HeatmapCell
    propertyName: string
  } | null>(null)

  return (
    <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
      {/* Legend bar */}
      <div className="flex items-center justify-between gap-4 border-b border-border/40 px-5 py-3">
        <div className="text-[11px] text-muted-foreground">
          <span className="font-medium text-foreground">{data.rows.length}</span>{' '}
          {data.rows.length === 1 ? 'property' : 'properties'} · next{' '}
          <span className="font-medium text-foreground">{data.days.length}</span> nights
        </div>
        <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          <span>Soft</span>
          <div className="flex h-3 overflow-hidden rounded">
            <div className="w-3 bg-slate-50" />
            <div className="w-3 bg-teal-50" />
            <div className="w-3 bg-teal-100" />
            <div className="w-3 bg-teal-200" />
            <div className="w-3 bg-teal-400" />
            <div className="w-3 bg-teal-600" />
            <div className="w-3 bg-teal-800" />
          </div>
          <span>Sold out</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-[760px] p-5">
          {/* Date header */}
          <div
            className="grid items-end gap-[3px]"
            style={{ gridTemplateColumns: `140px repeat(${data.days.length}, minmax(0, 1fr))` }}
          >
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Property
            </div>
            {data.days.map((d, i) => {
              const meta = formatDayShort(d)
              return (
                <div key={i} className="flex flex-col items-center">
                  <span
                    className={`text-[9px] font-semibold uppercase tracking-wider ${
                      meta.isWeekend ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  >
                    {meta.dow}
                  </span>
                  <span
                    className={`text-[11px] tabular-nums ${
                      meta.isToday
                        ? 'font-bold text-foreground'
                        : meta.isWeekend
                        ? 'font-semibold text-foreground'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {meta.day}
                  </span>
                  {meta.isToday && <div className="mt-0.5 h-0.5 w-3 rounded-full bg-primary" />}
                </div>
              )
            })}
          </div>

          {/* Rows */}
          <div className="mt-3 space-y-[3px]">
            {data.rows.map((row, rowIdx) => (
              <div
                key={row.propertyId}
                className="grid items-center gap-[3px]"
                style={{ gridTemplateColumns: `140px repeat(${data.days.length}, minmax(0, 1fr))` }}
              >
                {/* Inertia <Link>, like every other property link in the app. This was the one
                    raw <a> left — a full page load, so it would have kept "working" against the
                    transition redirect long after every SPA link had gone stale. */}
                <Link
                  href={row.propertyBasePath}
                  className="truncate pr-2 text-[12px] font-medium text-foreground hover:text-primary transition-colors"
                  title={row.propertyName}
                >
                  {row.propertyName}
                </Link>
                {row.cells.map((cell, colIdx) => {
                  const { bg, text } = cellColor(cell.occ)
                  const isHover = hover?.rowIdx === rowIdx && hover?.colIdx === colIdx
                  const meta = formatDayShort(cell.date)
                  return (
                    <div
                      key={colIdx}
                      className={`relative aspect-square ${bg} ${
                        meta.isToday ? 'ring-1 ring-inset ring-primary/60' : ''
                      } ${isHover ? 'ring-1 ring-inset ring-foreground/40' : ''} rounded-[3px] transition-all`}
                      onMouseEnter={() =>
                        setHover({ rowIdx, colIdx, cell, propertyName: row.propertyName })
                      }
                      onMouseLeave={() => setHover(null)}
                    >
                      {cell.occ > 0 && cell.occ < 25 && (
                        <span className={`absolute inset-0 flex items-center justify-center text-[9px] tabular-nums ${text} opacity-60`}>
                          {Math.round(cell.occ)}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Hover detail strip */}
      <div className="border-t border-border/40 bg-muted/40 px-5 py-3">
        {hover ? (
          <div className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-baseline gap-3">
              <span className="font-semibold tracking-tight text-foreground">{hover.propertyName}</span>
              <span className="text-muted-foreground">
                {new Date(hover.cell.date + 'T00:00:00').toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                })}
              </span>
            </div>
            <div className="flex items-baseline gap-4 tabular-nums">
              <span className="text-muted-foreground">
                <span className="font-semibold text-foreground">{hover.cell.booked}</span> /{' '}
                {hover.cell.capacity} rooms
              </span>
              <span className="text-base font-semibold text-foreground">
                {hover.cell.occ.toFixed(1)}%
              </span>
            </div>
          </div>
        ) : (
          <p className="text-xs italic text-muted-foreground">
            Hover any cell for the breakdown · weekends and today are highlighted
          </p>
        )}
      </div>
    </div>
  )
}
